/**
 * Tenant Context Provider
 *
 * Provides tenant context to the application including:
 * - Tenant information (id, slug, name, settings, features)
 * - AI access level based on user role
 * - Feature flag checking
 * - AI tool permission checking
 *
 * Part of: AI Platform Phase 3.0 - Multi-Tenant Foundation
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth, registerTenantOptionalHook } from './AuthContext';

// =============================================================================
// Types
// =============================================================================

export type AIAccessLevel = 'super_admin' | 'tenant_admin' | 'user' | 'none';

export interface TenantSettings {
  timezone: string;
  dateFormat: string;
  locale: string;
}

export interface TenantBranding {
  primaryColor: string;
  secondaryColor: string;
  logo: string | null;
  favicon: string | null;
  appName: string | null;
}

export interface TenantLimits {
  maxUsers: number;
  maxStorageGB: number;
  maxApiCallsPerDay: number;
}

export interface TenantAIConfig {
  enabled: boolean;
  modelsAllowed: string[];
  dailyTokenLimit: number;
  customGuidelines: string | null;
}

export interface TenantInfo {
  id: string;
  slug: string;
  name: string;
  isolationLevel: 'row' | 'schema' | 'container';
  status: 'provisioning' | 'active' | 'suspended' | 'archived';
  settings: TenantSettings;
  features: string[];
  vertical: string | null;
  branding: TenantBranding;
  limits: TenantLimits;
  aiConfig: TenantAIConfig;
}

export interface TenantContextValue {
  tenant: TenantInfo | null;
  tenantId: string | null;
  tenantSlug: string | null;
  aiAccessLevel: AIAccessLevel;
  isLoading: boolean;
  error: string | null;
  hasFeature: (feature: string) => boolean;
  canUseAITool: (tool: string) => boolean;
  isAIEnabled: () => boolean;
  getAllowedModels: () => string[];
  refreshTenant: () => Promise<void>;
}

// =============================================================================
// AI Tool Sets by Access Level
// =============================================================================

const SUPER_ADMIN_TOOLS = new Set(['*']);

const TENANT_ADMIN_TOOLS = new Set([
  // User management
  'list_users', 'create_user', 'update_user', 'delete_user', 'invite_user',
  // Tenant settings
  'get_settings', 'update_settings', 'get_branding', 'update_branding',
  // Reports and analytics
  'generate_report', 'get_analytics', 'export_data',
  // Logs and audit
  'query_logs', 'get_audit_trail', 'search_logs',
  // Configuration
  'configure_dashboard', 'manage_features', 'set_limits',
  // Documentation
  'search_documentation', 'get_help',
  // AI configuration
  'configure_ai', 'set_ai_guidelines',
]);

const USER_TOOLS = new Set([
  // Self-service
  'search_help', 'explain_feature', 'get_started_guide',
  // Own data
  'query_own_data', 'get_my_profile', 'update_my_settings',
  // Assistance
  'fill_form_assistance', 'workflow_guidance', 'suggest_next_action',
  // Documentation
  'search_docs', 'get_faq',
]);

// =============================================================================
// Context
// =============================================================================

const TenantContext = createContext<TenantContextValue | null>(null);

// =============================================================================
// Hook
// =============================================================================

export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

/**
 * Hook that returns tenant context if available, null otherwise.
 * Use this in components that can work without tenant context.
 */
export function useTenantOptional(): TenantContextValue | null {
  return useContext(TenantContext);
}

// Register this hook with AuthContext to enable useAuthWithTenant convenience hook
registerTenantOptionalHook(useTenantOptional as () => TenantContextValue | null);

// =============================================================================
// Provider
// =============================================================================

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps): React.ReactElement {
  const { accessToken, user, isAuthenticated, authFetch } = useAuth();

  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);
  const [aiAccessLevel, setAIAccessLevel] = useState<AIAccessLevel>('none');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Determine AI access level from user role
   */
  const determineAIAccessLevel = useCallback((role: string | undefined): AIAccessLevel => {
    if (!role) return 'none';

    const normalizedRole = role.toLowerCase();

    if (normalizedRole === 'super_admin' || normalizedRole === 'superadmin') {
      return 'super_admin';
    }
    if (normalizedRole === 'admin' || normalizedRole === 'tenant_admin') {
      return 'tenant_admin';
    }
    if (normalizedRole === 'user' || normalizedRole === 'member') {
      return 'user';
    }

    return 'none';
  }, []);

  /**
   * Parse JWT token to extract tenant claims
   */
  const parseTokenClaims = useCallback((token: string): { tid: string | null; org: string | null } => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        tid: payload.tid || payload.tenant_id || null,
        org: payload.org || payload.tenant_slug || null,
      };
    } catch (e) {
      console.error('[TenantContext] Failed to parse JWT:', e);
      return { tid: null, org: null };
    }
  }, []);

  /**
   * Fetch full tenant details from API
   */
  const fetchTenantDetails = useCallback(async (id: string): Promise<TenantInfo | null> => {
    try {
      const response = await authFetch(`/api/admin/tenants/${id}`);

      if (response.ok) {
        const data = await response.json();
        return {
          id: data.id,
          slug: data.slug,
          name: data.name,
          isolationLevel: data.isolation_level,
          status: data.status,
          settings: {
            timezone: data.settings?.timezone || 'UTC',
            dateFormat: data.settings?.dateFormat || 'YYYY-MM-DD',
            locale: data.settings?.locale || 'en-US',
          },
          features: data.features || [],
          vertical: data.vertical || null,
          branding: {
            primaryColor: data.branding?.primaryColor || '#7c3aed',
            secondaryColor: data.branding?.secondaryColor || '#06b6d4',
            logo: data.branding?.logo || null,
            favicon: data.branding?.favicon || null,
            appName: data.branding?.appName || null,
          },
          limits: {
            maxUsers: data.limits?.maxUsers || 100,
            maxStorageGB: data.limits?.maxStorageGB || 50,
            maxApiCallsPerDay: data.limits?.maxApiCallsPerDay || 10000,
          },
          aiConfig: {
            enabled: data.ai_config?.enabled ?? true,
            modelsAllowed: data.ai_config?.modelsAllowed || ['claude-3-5-sonnet'],
            dailyTokenLimit: data.ai_config?.dailyTokenLimit || 100000,
            customGuidelines: data.ai_config?.customGuidelines || null,
          },
        };
      }

      // 403/404 means user doesn't have access to tenant details (normal for non-admins)
      if (response.status === 403 || response.status === 404) {
        console.log('[TenantContext] No access to tenant details (normal for non-admin users)');
        return null;
      }

      console.warn('[TenantContext] Failed to fetch tenant:', response.status);
      return null;
    } catch (e) {
      console.error('[TenantContext] Error fetching tenant:', e);
      return null;
    }
  }, [authFetch]);

  /**
   * Refresh tenant information
   */
  const refreshTenant = useCallback(async (): Promise<void> => {
    if (!accessToken || !isAuthenticated) {
      setTenant(null);
      setTenantId(null);
      setTenantSlug(null);
      setAIAccessLevel('none');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Parse JWT for tenant claims
      const { tid, org } = parseTokenClaims(accessToken);
      setTenantId(tid);
      setTenantSlug(org);

      // Determine AI access level from user role
      const level = determineAIAccessLevel(user?.role);
      setAIAccessLevel(level);

      // If we have a tenant ID, try to fetch full details
      // Only super_admin and tenant_admin can access tenant details
      if (tid && (level === 'super_admin' || level === 'tenant_admin')) {
        const tenantInfo = await fetchTenantDetails(tid);
        setTenant(tenantInfo);
      } else {
        // For regular users, create minimal tenant info from JWT claims
        if (tid && org) {
          setTenant({
            id: tid,
            slug: org,
            name: org, // Use slug as name fallback
            isolationLevel: 'row',
            status: 'active',
            settings: {
              timezone: 'UTC',
              dateFormat: 'YYYY-MM-DD',
              locale: 'en-US',
            },
            features: [],
            vertical: null,
            branding: {
              primaryColor: '#7c3aed',
              secondaryColor: '#06b6d4',
              logo: null,
              favicon: null,
              appName: null,
            },
            limits: {
              maxUsers: 100,
              maxStorageGB: 50,
              maxApiCallsPerDay: 10000,
            },
            aiConfig: {
              enabled: true,
              modelsAllowed: ['claude-3-5-sonnet'],
              dailyTokenLimit: 100000,
              customGuidelines: null,
            },
          });
        } else {
          setTenant(null);
        }
      }
    } catch (e) {
      console.error('[TenantContext] Error refreshing tenant:', e);
      setError('Failed to load tenant information');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, isAuthenticated, user?.role, parseTokenClaims, determineAIAccessLevel, fetchTenantDetails]);

  /**
   * Check if tenant has a specific feature enabled
   */
  const hasFeature = useCallback((feature: string): boolean => {
    if (!tenant) return false;
    return tenant.features.includes(feature);
  }, [tenant]);

  /**
   * Check if user can use a specific AI tool
   */
  const canUseAITool = useCallback((tool: string): boolean => {
    // Check if AI is enabled for tenant (super admin bypasses this)
    if (aiAccessLevel !== 'super_admin' && tenant && !tenant.aiConfig.enabled) {
      return false;
    }

    // Check tool permissions by access level
    switch (aiAccessLevel) {
      case 'super_admin':
        // Super admin has wildcard access
        return SUPER_ADMIN_TOOLS.has('*') || SUPER_ADMIN_TOOLS.has(tool);
      case 'tenant_admin':
        return TENANT_ADMIN_TOOLS.has(tool) || USER_TOOLS.has(tool);
      case 'user':
        return USER_TOOLS.has(tool);
      default:
        return false;
    }
  }, [aiAccessLevel, tenant]);

  /**
   * Check if AI is enabled for the current tenant
   */
  const isAIEnabled = useCallback((): boolean => {
    // Super admins always have AI access
    if (aiAccessLevel === 'super_admin') {
      return true;
    }
    // Check tenant AI config
    return tenant?.aiConfig?.enabled ?? false;
  }, [aiAccessLevel, tenant]);

  /**
   * Get list of allowed AI models for the current tenant
   */
  const getAllowedModels = useCallback((): string[] => {
    // Super admins have access to all models
    if (aiAccessLevel === 'super_admin') {
      return ['*'];
    }
    return tenant?.aiConfig?.modelsAllowed ?? [];
  }, [aiAccessLevel, tenant]);

  // Load tenant info when auth state changes
  useEffect(() => {
    refreshTenant();
  }, [refreshTenant]);

  const value: TenantContextValue = {
    tenant,
    tenantId,
    tenantSlug,
    aiAccessLevel,
    isLoading,
    error,
    hasFeature,
    canUseAITool,
    isAIEnabled,
    getAllowedModels,
    refreshTenant,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export default TenantContext;
